import { css } from '@emotion/react';
import { theme } from 'global';
import {
  fieldErrorIcon,
  progressTrack as baseProgressTrack,
  shimmer,
} from './steps/ActivationStep.styles';

export { progressFill, title } from './steps/ActivationStep.styles';

const radioGroupError = css`
  .MuiFormHelperText-root.Mui-error {
    color: ${theme.colors.danger};
    display: flex;
    align-items: baseline;
    letter-spacing: 0;
    margin: 4px 0 0;

    .errorIcon {
      ${fieldErrorIcon};
    }
  }
`;

export const pageWrapper = css`
  min-height: calc(100vh - 56px);
  background: ${theme.colors.white};
  display: flex;
  justify-content: center;
  padding: 32px 16px 56px;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    padding: 48px 24px 64px;
  }

  @media screen and (max-width: ${theme.breaks.xs}px) {
    padding-bottom: 96px;
  }
`;

export const contentPanel = css`
  width: 100%;
  max-width: 600px;

  h1 {
    color: ${theme.colors.secondary};
    margin: 0 0 20px;
  }
`;

export const stepDescription = css`
  color: ${theme.colors.neutral[500]};
  margin: 0 0 20px;
`;

export const stepOneRoot = css`
  min-height: calc(100vh - 56px);
  background: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const stepOneAIBanner = css`
  width: 100%;
  border: 0;
  border-radius: 0;
  background: linear-gradient(
    90deg,
    rgba(237, 230, 255, 0.2) 0%,
    rgba(170, 143, 255, 0.2) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 48px;
  padding: 12px 16px;
  box-sizing: border-box;
`;

export const stepOneAIIcon = () => css`
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
`;

export const stepOneBannerCopy = css`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    align-items: center;
    flex-direction: column;
    gap: 0;
  }
`;

export const stepOneBannerTitle = css`
  color: ${theme.colors.primary.medium};
  margin: 0;
`;

export const stepOneBannerSubtitle = css`
  color: ${theme.colors.neutral[600]};
  margin: 0;
`;

export const stepOnePageWrapper = css`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const stepOnePanel = css`
  width: 100%;
  max-width: 600px;
  padding: 32px;
  box-sizing: border-box;
  background: ${theme.colors.white};
  border-radius: 12px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    padding: 24px 16px 96px;
  }
`;

export const stepOneHeader = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const stepOneTitle = css`
  color: ${theme.colors.secondary};
  letter-spacing: 0.3px;
  margin: 0;
`;

export const stepOneForm = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 4px;

  .MuiFormControl-root {
    margin: 0;
  }

  ${radioGroupError};
`;

export const stepOneSection = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const stepOneAddressField = css`
  .MuiInputLabel-root {
    color: ${theme.colors.neutral[500]};
  }

  .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
    transform: translate(14px, 17.5px) scale(1);
  }

  .MuiInputLabel-root.MuiInputLabel-shrink {
    transform: translate(14px, -6px) scale(0.75);
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${theme.colors.primary.medium};
  }

  .MuiOutlinedInput-root {
    color: ${theme.colors.neutral[600]};
    border-radius: 4px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.colors.neutral[500]};
  }

  .MuiFormHelperText-root {
    color: ${theme.colors.neutral[500]};
    letter-spacing: 0.4px;
    margin: 4px 16px 0 !important;
  }

  .Mui-error + .MuiFormHelperText-root,
  .MuiFormHelperText-root.Mui-error {
    color: ${theme.colors.danger};
  }

  margin-bottom: 20px;
`;

export const stepOneRadioField = css`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const stepOneRadioGroup = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

export const stepOneRadioOption = (selected = false) => css`
  width: 100%;
  min-height: 48px;
  border: 1px solid
    ${selected ? theme.colors.primary.medium : theme.colors.neutral[200]};
  background: ${selected ? theme.colors.primary.lightest : theme.colors.white};
  border-radius: ${theme.radii.lg};
  box-sizing: border-box;
  margin: 0;
  padding: 0 12px;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    border-color: ${selected
      ? theme.colors.primary.medium
      : theme.colors.neutral[200]};
    background: ${selected
      ? theme.colors.primary.lightest
      : theme.colors.neutral[0]};
  }

  > span:first-of-type {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .MuiRadio-root {
    padding: 0;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .MuiRadio-root svg {
    width: 24px;
    height: 24px;
  }

  .MuiRadio-root:not(.Mui-checked) svg {
    color: ${theme.colors.neutral[300]};
  }

  .MuiFormControlLabel-label {
    margin-left: 0 !important;
    color: ${theme.colors.neutral[600]};
  }
`;

export const stepOneSkeleton = (height: number) => css`
  width: 100%;
  height: ${height}px;
  background: ${theme.colors.neutral[0]};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 40%;
    background: linear-gradient(
      90deg,
      rgba(230, 230, 230, 0.01) 0%,
      #e6e6e6 50%,
      rgba(230, 230, 230, 0.01) 100%
    );
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
`;

// MC-4775 TEMP HARDCODED PROGRESS BAR FOR ACTIVATION STEPS
export const progressTrack = css`
  ${baseProgressTrack};
  margin-bottom: 40px;
`;
